/** Bedingte Absätze: Wert vorhanden, leer, Zahl, Text oder Datum. */

const EMPTY_VALUES = new Set(["", "0", "0.0", "0.00", "0,00", "-", "–", "—"]);

const IF_TOKEN_RE =
  /\{\{\s*#(if|unless)\s+([a-zA-Z0-9_]+)([^}]*)\}\}((?:(?!\{\{\s*#(?:if|unless)\b)[\s\S])*?)\{\{\s*\/\1\s*\}\}/gi;

const DIV_OPEN_RE = /<\s*div\b([^>]*)>/gi;
const DIV_CLOSE_RE = /<\s*\/\s*div\s*>/gi;
const MAIL_IF_ATTR_RE = /data-mail-(if|unless)\s*=\s*["']([a-zA-Z0-9_]+)["']/i;
const TAG_RE = /<[^>]+>/g;
const ANY_TOKEN_RE = /\{\{[^}]+\}\}/g;
const GREETING_RE =
  /^(?:sehr\s+geehrte\w*|guten\s+tag|guten\s+morgen|guten\s+abend|hallo|liebe\w*|dear|hello|hi)\b/i;
const CLOSING_RE = /gr(?:ü|ue)(?:ß|ss)e?n?\b|kind regards|best regards|sincerely/i;
const SALUTATION_TOKEN_RE = /\{\{\s*salutation\s*\}\}/;

export type MailRuleOp =
  | "filled"
  | "empty"
  | "eq"
  | "ne"
  | "lt"
  | "lte"
  | "gt"
  | "gte"
  | "between"
  | "contains"
  | "not_contains"
  | "starts"
  | "ends"
  | "oneof"
  | "before"
  | "after"
  | "on";

export type MailRule = {
  key: string;
  op: MailRuleOp;
  a: string;
  b: string;
};

const KNOWN_OPS = new Set<string>([
  "filled",
  "empty",
  "eq",
  "ne",
  "lt",
  "lte",
  "gt",
  "gte",
  "between",
  "contains",
  "not_contains",
  "starts",
  "ends",
  "oneof",
  "before",
  "after",
  "on",
]);

const WORD_OPS: { prefix: string; op: MailRuleOp }[] = [
  { prefix: "notcontains", op: "not_contains" },
  { prefix: "contains", op: "contains" },
  { prefix: "between", op: "between" },
  { prefix: "starts", op: "starts" },
  { prefix: "ends", op: "ends" },
  { prefix: "oneof", op: "oneof" },
  { prefix: "before", op: "before" },
  { prefix: "after", op: "after" },
  { prefix: "on", op: "on" },
];

const SYMBOL_OPS: { prefix: string; op: MailRuleOp }[] = [
  { prefix: "<=", op: "lte" },
  { prefix: ">=", op: "gte" },
  { prefix: "!=", op: "ne" },
  { prefix: "<", op: "lt" },
  { prefix: ">", op: "gt" },
  { prefix: "=", op: "eq" },
];

export const MAIL_RULE_OPERATORS: { op: MailRuleOp; label: string; group: string }[] = [
  { op: "filled", label: "einen Wert hat", group: "Allgemein" },
  { op: "empty", label: "leer ist", group: "Allgemein" },
  { op: "eq", label: "genau", group: "Allgemein" },
  { op: "ne", label: "nicht", group: "Allgemein" },
  { op: "lt", label: "kleiner als", group: "Zahl" },
  { op: "lte", label: "höchstens", group: "Zahl" },
  { op: "gt", label: "größer als", group: "Zahl" },
  { op: "gte", label: "mindestens", group: "Zahl" },
  { op: "between", label: "zwischen", group: "Zahl" },
  { op: "contains", label: "enthält", group: "Text" },
  { op: "not_contains", label: "enthält nicht", group: "Text" },
  { op: "starts", label: "beginnt mit", group: "Text" },
  { op: "ends", label: "endet auf", group: "Text" },
  { op: "oneof", label: "ist eines von", group: "Text" },
  { op: "on", label: "ist am", group: "Datum" },
  { op: "before", label: "liegt vor", group: "Datum" },
  { op: "after", label: "liegt nach", group: "Datum" },
];

export function placeholderIsFilled(value: string | null | undefined): boolean {
  return !EMPTY_VALUES.has((value ?? "").trim());
}

export function parseWholeNumber(value: string | null | undefined): number | null {
  const text = (value ?? "").trim();
  const match = /^(-?\d[\d.,]*)\s*(?:€|eur|euro|m|meter)?$/i.exec(text);
  if (!match?.[1]) return null;
  return numericToken(match[1]);
}

function numericToken(token: string): number | null {
  let text = token;
  if (/^-?\d{1,3}(?:\.\d{3})+$/.test(text)) text = text.replace(/\./g, "");
  else if (/^-?\d{1,3}(?:,\d{3})+$/.test(text)) text = text.replace(/,/g, "");
  else if (text.includes(",") && text.includes(".")) {
    if (text.lastIndexOf(",") > text.lastIndexOf(".")) text = text.replace(/\./g, "").replace(",", ".");
    else text = text.replace(/,/g, "");
  } else if (text.includes(",")) text = text.replace(/,/g, ".");
  else if ((text.match(/\./g) ?? []).length > 1) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

export function parseDate(value: string | null | undefined): Date | null {
  const text = (value ?? "").trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  const german = iso ? null : /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(text);
  if (!iso && !german) return null;
  const year = Number(iso?.[1] ?? german?.[3]);
  const month = Number(iso?.[2] ?? german?.[2]);
  const day = Number(iso?.[3] ?? german?.[1]);
  if (!year || !month || !day) return null;
  const result = new Date(Date.UTC(year, month - 1, day));
  if (result.getUTCFullYear() !== year || result.getUTCMonth() !== month - 1 || result.getUTCDate() !== day) {
    return null;
  }
  return result;
}

function splitBetween(rest: string): [string, string] {
  const und = rest.trim().split(/\s+und\s+/i);
  if (und.length === 2) return [und[0]?.trim() ?? "", und[1]?.trim() ?? ""];
  const bits = rest.trim().split(/\s+/);
  if (bits.length >= 2) return [bits[0] ?? "", bits.slice(1).join(" ")];
  return [rest.trim(), ""];
}

export function parseIfToken(kind: string, key: string, expression: string): MailRule {
  const text = expression.trim();
  if (!text) return { key, op: kind.toLowerCase() === "unless" ? "empty" : "filled", a: "", b: "" };
  const folded = text.toLowerCase();
  for (const item of WORD_OPS) {
    if (folded === item.prefix || folded.startsWith(`${item.prefix} `)) {
      const rest = text.slice(item.prefix.length).trim();
      if (item.op === "between") {
        const [left, right] = splitBetween(rest);
        return { key, op: item.op, a: left, b: right };
      }
      return { key, op: item.op, a: rest, b: "" };
    }
  }
  for (const item of SYMBOL_OPS) {
    if (text.startsWith(item.prefix)) return { key, op: item.op, a: text.slice(item.prefix.length).trim(), b: "" };
  }
  return { key, op: "eq", a: text, b: "" };
}

export function ruleValueMode(op: MailRuleOp): "none" | "one" | "two" {
  if (op === "filled" || op === "empty") return "none";
  if (op === "between") return "two";
  return "one";
}

function expressionFor(rule: MailRule): string {
  switch (rule.op) {
    case "eq":
      return `= ${rule.a}`;
    case "ne":
      return `!= ${rule.a}`;
    case "lt":
      return `< ${rule.a}`;
    case "lte":
      return `<= ${rule.a}`;
    case "gt":
      return `> ${rule.a}`;
    case "gte":
      return `>= ${rule.a}`;
    case "between":
      return `between ${rule.a} ${rule.b}`;
    case "contains":
      return `contains ${rule.a}`;
    case "not_contains":
      return `notcontains ${rule.a}`;
    case "starts":
      return `starts ${rule.a}`;
    case "ends":
      return `ends ${rule.a}`;
    case "oneof":
      return `oneof ${rule.a}`;
    case "before":
      return `before ${rule.a}`;
    case "after":
      return `after ${rule.a}`;
    case "on":
      return `on ${rule.a}`;
    default:
      return "";
  }
}

export function openToken(rule: MailRule): string {
  if (rule.op === "empty") return `{{#unless ${rule.key}}}`;
  if (rule.op === "filled") return `{{#if ${rule.key}}}`;
  return `{{#if ${rule.key} ${expressionFor(rule)}}}`;
}

export function closeToken(rule: MailRule): string {
  return rule.op === "empty" ? "{{/unless}}" : "{{/if}}";
}

function quoted(value: string): string {
  return parseWholeNumber(value) != null || parseDate(value) != null ? value : `„${value}“`;
}

function pluralVerb(value: string): boolean {
  return parseDate(value) == null && parseWholeNumber(value) != null;
}

export function rulePhrase(rule: MailRule, label: string): string {
  const a = rule.a.trim();
  const b = rule.b.trim();
  switch (rule.op) {
    case "filled":
      return `Nur wenn ${label} einen Wert hat`;
    case "empty":
      return `Nur wenn ${label} leer ist`;
    case "eq":
      return pluralVerb(a) ? `Nur wenn ${label} genau ${a} sind` : `Nur wenn ${label} genau ${quoted(a)} ist`;
    case "ne":
      return pluralVerb(a) ? `Nur wenn ${label} nicht ${a} sind` : `Nur wenn ${label} nicht ${quoted(a)} ist`;
    case "lt":
      return `Nur wenn ${label} kleiner als ${a} ${pluralVerb(a) ? "sind" : "ist"}`;
    case "lte":
      return `Nur wenn ${label} höchstens ${a} ${pluralVerb(a) ? "sind" : "ist"}`;
    case "gt":
      return `Nur wenn ${label} größer als ${a} ${pluralVerb(a) ? "sind" : "ist"}`;
    case "gte":
      return `Nur wenn ${label} mindestens ${a} ${pluralVerb(a) ? "sind" : "ist"}`;
    case "between":
      return `Nur wenn ${label} zwischen ${a} und ${b} ${pluralVerb(a) ? "liegen" : "liegt"}`;
    case "contains":
      return `Nur wenn ${label} ${quoted(a)} enthält`;
    case "not_contains":
      return `Nur wenn ${label} ${quoted(a)} nicht enthält`;
    case "starts":
      return `Nur wenn ${label} mit ${quoted(a)} beginnt`;
    case "ends":
      return `Nur wenn ${label} auf ${quoted(a)} endet`;
    case "oneof":
      return `Nur wenn ${label} eines von ${a} ist`;
    case "before":
      return `Nur wenn ${label} vor dem ${a} liegt`;
    case "after":
      return `Nur wenn ${label} nach dem ${a} liegt`;
    case "on":
      return `Nur wenn ${label} am ${a} liegt`;
    default:
      return `Nur wenn ${label} einen Wert hat`;
  }
}

export function ruleSentence(rule: MailRule, label: string): string {
  const phrase = rulePhrase(rule, label).replace(/^Nur wenn/, "solange");
  return `Der Absatz bleibt in der Mail, ${phrase}.`;
}

function sameNumber(left: number, right: number): boolean {
  return Math.abs(left - right) < 1e-9;
}

function compareOrdered(left: number, right: number, rightB: number | null, op: MailRuleOp): boolean {
  if (op === "lt") return left < right;
  if (op === "lte") return left < right || sameNumber(left, right);
  if (op === "gt") return left > right;
  if (op === "gte") return left > right || sameNumber(left, right);
  if (op === "eq") return sameNumber(left, right);
  if (op === "ne") return !sameNumber(left, right);
  if (op === "between") {
    const other = rightB ?? right;
    const low = Math.min(right, other);
    const high = Math.max(right, other);
    return (left > low || sameNumber(left, low)) && (left < high || sameNumber(left, high));
  }
  return false;
}

export function ruleMatches(rule: MailRule, values: Record<string, string>): boolean {
  if (!KNOWN_OPS.has(rule.op) || !rule.key) return false;
  const raw = values[rule.key] ?? "";
  if (rule.op === "filled") return placeholderIsFilled(raw);
  if (rule.op === "empty") return !placeholderIsFilled(raw);
  const text = raw.trim();
  const needle = rule.a.trim();
  const folded = text.toLowerCase();
  const foldedNeedle = needle.toLowerCase();
  if (rule.op === "contains") return Boolean(foldedNeedle) && folded.includes(foldedNeedle);
  if (rule.op === "not_contains") return !foldedNeedle || !folded.includes(foldedNeedle);
  if (rule.op === "starts") return Boolean(foldedNeedle) && folded.startsWith(foldedNeedle);
  if (rule.op === "ends") return Boolean(foldedNeedle) && folded.endsWith(foldedNeedle);
  if (rule.op === "oneof") {
    const choices = needle.split(/[,;]/).map((item) => item.trim()).filter(Boolean);
    if (choices.length === 0) return false;
    const leftNumber = parseWholeNumber(text);
    return choices.some((choice) => {
      if (leftNumber != null) {
        const rightNumber = parseWholeNumber(choice);
        if (rightNumber != null && sameNumber(leftNumber, rightNumber)) return true;
      }
      return choice.toLowerCase() === folded;
    });
  }
  if (rule.op === "before" || rule.op === "after" || rule.op === "on") {
    const left = parseDate(text);
    const right = parseDate(needle);
    if (!left || !right) return false;
    if (rule.op === "before") return left.getTime() < right.getTime();
    if (rule.op === "after") return left.getTime() > right.getTime();
    return left.getTime() === right.getTime();
  }
  const leftNumber = parseWholeNumber(text);
  const rightNumber = parseWholeNumber(needle);
  const rightB = rule.op === "between" ? parseWholeNumber(rule.b) : null;
  if (leftNumber != null && rightNumber != null && (rule.op !== "between" || rightB != null)) {
    return compareOrdered(leftNumber, rightNumber, rightB, rule.op);
  }
  const leftDate = parseDate(text);
  const rightDate = parseDate(needle);
  const rightDateB = rule.op === "between" ? parseDate(rule.b) : null;
  if (leftDate && rightDate && (rule.op !== "between" || rightDateB)) {
    return compareOrdered(leftDate.getTime(), rightDate.getTime(), rightDateB ? rightDateB.getTime() : null, rule.op);
  }
  if (rule.op === "eq") return Boolean(foldedNeedle) && folded === foldedNeedle;
  if (rule.op === "ne") return folded !== foldedNeedle;
  return false;
}

function shiftNumber(value: string, delta: number): string | null {
  const number = parseWholeNumber(value);
  if (number == null) return null;
  const next = number + delta;
  return Number.isInteger(next) ? String(next) : String(next);
}

function formatDate(value: Date): string {
  const day = String(value.getUTCDate()).padStart(2, "0");
  const month = String(value.getUTCMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${value.getUTCFullYear()}`;
}

function shiftDate(value: string, deltaDays: number): string | null {
  const parsed = parseDate(value);
  if (!parsed) return null;
  parsed.setUTCDate(parsed.getUTCDate() + deltaDays);
  return formatDate(parsed);
}

function satisfyingValue(rule: MailRule): string {
  const numberDown = shiftNumber(rule.a, -1);
  const numberUp = shiftNumber(rule.a, 1);
  const dateDown = shiftDate(rule.a, -1);
  const dateUp = shiftDate(rule.a, 1);
  switch (rule.op) {
    case "filled":
      return "1";
    case "empty":
    case "not_contains":
      return "";
    case "eq":
    case "lte":
    case "gte":
    case "on":
      return rule.a || "1";
    case "ne":
      return numberUp ?? (rule.a === "x" ? "y" : "x");
    case "lt":
      return numberDown ?? dateDown ?? "0";
    case "gt":
      return numberUp ?? dateUp ?? "1";
    case "between":
      return rule.a || rule.b || "1";
    case "contains":
    case "starts":
    case "ends":
      return rule.a || "x";
    case "oneof":
      return rule.a.split(/[,;]/).map((item) => item.trim()).find(Boolean) ?? "x";
    case "before":
      return dateDown ?? "01.01.2000";
    case "after":
      return dateUp ?? "31.12.2099";
    default:
      return "1";
  }
}

function failingValue(rule: MailRule): string {
  const numberDown = shiftNumber(rule.a, -1);
  const numberUp = shiftNumber(rule.a, 1);
  const dateDown = shiftDate(rule.a, -1);
  const dateUp = shiftDate(rule.a, 1);
  switch (rule.op) {
    case "filled":
    case "contains":
      return "";
    case "empty":
      return "1";
    case "eq":
      return numberUp ?? (rule.a === "0" ? "1" : "0");
    case "ne":
    case "lt":
    case "gt":
    case "before":
    case "after":
      return rule.a || "0";
    case "lte":
      return numberUp ?? dateUp ?? "99999";
    case "gte":
      return numberDown ?? dateDown ?? "0";
    case "between": {
      const left = parseWholeNumber(rule.a);
      const right = parseWholeNumber(rule.b);
      if (left != null && right != null) return String(Math.min(left, right) - 1);
      const earlier = (parseDate(rule.a)?.getTime() ?? 0) <= (parseDate(rule.b)?.getTime() ?? 0) ? rule.a : rule.b;
      return shiftDate(earlier, -1) ?? "0";
    }
    case "not_contains":
    case "starts":
    case "ends":
    case "oneof":
      return rule.a ? `x${rule.a}x` : "x";
    case "on":
      return dateUp ?? "01.01.2000";
    default:
      return "";
  }
}

export function valuesForPreviewRules(
  values: Record<string, string>,
  rules: MailRule[],
  enabled: boolean[],
): Record<string, string> {
  const next = { ...values };
  rules.forEach((rule, index) => {
    const on = enabled[index] !== false;
    const holds = ruleMatches(rule, next);
    if (on && !holds) next[rule.key] = satisfyingValue(rule);
    if (!on && holds) next[rule.key] = failingValue(rule);
  });
  return next;
}

function decodeAttr(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

function ruleFromAttrs(attrs: string): MailRule | null {
  const kind = MAIL_IF_ATTR_RE.exec(attrs);
  if (!kind?.[2]) return null;
  const found: Record<string, string> = {};
  for (const match of attrs.matchAll(/([a-zA-Z0-9_-]+)\s*=\s*["']([^"']*)["']/g)) {
    if (match[1]) found[match[1].toLowerCase()] = decodeAttr(match[2] ?? "");
  }
  const op = found["data-mail-op"] ?? "";
  if (op && KNOWN_OPS.has(op)) {
    return { key: kind[2], op: op as MailRuleOp, a: found["data-mail-a"] ?? "", b: found["data-mail-b"] ?? "" };
  }
  return parseIfToken(kind[1] ?? "if", kind[2], "");
}

function conditionVisible(rule: MailRule, values: Record<string, string>): boolean {
  return ruleMatches(rule, values);
}

function applyHtmlConditions(
  template: string,
  values: Record<string, string> | null,
  dropAll = false,
): string {
  const parts: string[] = [];
  let cursor = 0;
  while (cursor < template.length) {
    DIV_OPEN_RE.lastIndex = cursor;
    const match = DIV_OPEN_RE.exec(template);
    if (!match) {
      parts.push(template.slice(cursor));
      break;
    }
    const rule = ruleFromAttrs(match[1] ?? "");
    if (!rule) {
      parts.push(template.slice(cursor, match.index + match[0].length));
      cursor = match.index + match[0].length;
      continue;
    }
    let depth = 1;
    let pos = match.index + match[0].length;
    let innerEnd: number | null = null;
    let end: number | null = null;
    while (depth) {
      DIV_OPEN_RE.lastIndex = pos;
      DIV_CLOSE_RE.lastIndex = pos;
      const nextOpen = DIV_OPEN_RE.exec(template);
      const nextClose = DIV_CLOSE_RE.exec(template);
      if (!nextClose) break;
      if (nextOpen && nextOpen.index < nextClose.index) {
        depth += 1;
        pos = nextOpen.index + nextOpen[0].length;
        continue;
      }
      depth -= 1;
      if (depth === 0) {
        innerEnd = nextClose.index;
        end = nextClose.index + nextClose[0].length;
      } else {
        pos = nextClose.index + nextClose[0].length;
      }
    }
    if (end == null || innerEnd == null) {
      parts.push(template.slice(cursor));
      break;
    }
    parts.push(template.slice(cursor, match.index));
    const inner = template.slice(match.index + match[0].length, innerEnd);
    if (!dropAll && values && conditionVisible(rule, values)) parts.push(inner);
    cursor = end;
  }
  return parts.join("");
}

function applyTokenConditions(
  template: string,
  values: Record<string, string> | null,
  dropAll = false,
): string {
  let current = template;
  for (let pass = 0; pass < 8; pass += 1) {
    IF_TOKEN_RE.lastIndex = 0;
    const updated = current.replace(IF_TOKEN_RE, (_match, kind: string, key: string, expression: string, inner: string) => {
      if (dropAll || !values || !ruleMatches(parseIfToken(kind, key, expression ?? ""), values)) return "";
      return inner.replace(/^\n+/, "").replace(/\n+$/, "");
    });
    if (updated === current) return current;
    current = updated;
  }
  return current;
}

export function applyTemplateConditions(template: string, values: Record<string, string>): string {
  let current = template;
  for (let pass = 0; pass < 8; pass += 1) {
    const updated = applyTokenConditions(applyHtmlConditions(current, values), values).replace(/\n{3,}/g, "\n\n");
    if (updated === current) return current;
    current = updated;
  }
  return current;
}

function textOutsideConditions(value: string): string {
  let current = value;
  for (let pass = 0; pass < 8; pass += 1) {
    const updated = applyTokenConditions(applyHtmlConditions(current, null, true), null, true);
    if (updated === current) return current;
    current = updated;
  }
  return current;
}

function visibleTemplateText(value: string): string {
  return textOutsideConditions(value).replace(TAG_RE, " ").replace(ANY_TOKEN_RE, " ").replace(/\s+/g, " ").trim();
}

export function templateIncludesSalutation(value: string | null | undefined): boolean {
  if (!value) return false;
  if (SALUTATION_TOKEN_RE.test(textOutsideConditions(value))) return true;
  return GREETING_RE.test(visibleTemplateText(value));
}

export function templateIncludesSignature(value: string | null | undefined): boolean {
  if (!value) return false;
  return CLOSING_RE.test(visibleTemplateText(value));
}

export function listMailRules(template: string): MailRule[] {
  const rules: MailRule[] = [];
  let current = template;
  for (let pass = 0; pass < 8; pass += 1) {
    const tokenRe =
      /\{\{\s*#(if|unless)\s+([a-zA-Z0-9_]+)([^}]*)\}\}((?:(?!\{\{\s*#(?:if|unless)\b)[\s\S])*?)\{\{\s*\/\1\s*\}\}/gi;
    const found = [...current.matchAll(tokenRe)];
    if (found.length === 0) break;
    for (const match of found) {
      rules.push(parseIfToken(match[1] ?? "if", match[2] ?? "", match[3] ?? ""));
    }
    current = current.replace(tokenRe, "");
  }
  const divRe = /<\s*div\b([^>]*)>/gi;
  for (const match of template.matchAll(divRe)) {
    const rule = ruleFromAttrs(match[1] ?? "");
    if (rule) rules.push(rule);
  }
  return rules;
}

export function listConditionKeys(template: string): string[] {
  const keys = new Set<string>();
  for (const rule of listMailRules(template)) {
    if (rule.key) keys.add(rule.key);
  }
  return [...keys];
}
